<template>
	<div class="homeview">
		<header ref="header">
			<h1>DURSS BOXES</h1>
			<span class="subtitle">handcrafted wooden puzzle boxes</span>
		</header>

		<div class="boxes">
			<div class="list">
				<HomeBox class="box"
					ref="box"
					@close="onClose"
					v-for="b in boxList"
					:key="b.id"
					:boxID="b.id"
					:title="b.title"
					:to="{name:'info', params:{id:b.id}}"
				/>
			</div>

			<div :class="navClasses" ref="nav">
				<button class="prev" @click="prev()">
					<img src="@/assets/icons/arrow_prev.svg" alt="prev">
				</button>
				<button class="next" @click="next()">
					<img src="@/assets/icons/arrow_next.svg" alt="next">
				</button>
			</div>
		</div>

	</div>
</template>

<script setup lang="ts">
import HomeBox from '@/components/HomeBox.vue';
import { boxStore as useBoxStore } from '@/stores/boxes';
import gsap from 'gsap/all';
import { computed, onBeforeMount, onMounted, ref, useTemplateRef } from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';

const router = useRouter();
const boxStore = useBoxStore();

const header = useTemplateRef<HTMLElement>('header');
const nav = useTemplateRef<HTMLDivElement>('nav');
const box = useTemplateRef<InstanceType<typeof HomeBox>[]>('box');

const currentIndex = ref<number>(0);

const boxList = ref<{id:string, title:string}[]>([]);

const navClasses = computed<string[]>(() => {
	const res = ["navigation"]
	if(currentIndex.value == 0) res.push("noPrev");
	return res;
});

onBeforeMount(() => {
	boxList.value = boxStore.items;
});

onMounted(() => {
	const boxes = box.value!;
	gsap.from(header.value!, {duration:2, opacity:0, clearProps:"all"});
	gsap.from(boxes.map(v => v.$el), {duration:2, opacity:0, delay:.5, y:100, clearProps:"all"});
	gsap.from(nav.value!, {duration:2, opacity:0, delay:1, width:"100vw", maxWidth:"120vw", clearProps:"all"});

	for (let i = 0; i < boxes.length; i++) {
		boxes[i].open();
	}
});

function onClose(to:RouteLocationRaw):void {
	const boxes = box.value!;
	gsap.to(header.value!, {duration:.5, opacity:0, delay:.5});
	gsap.to(boxes.map(v => v.$el), {duration:1, opacity:0, scale:.8, ease:"sine.in"});
	gsap.to(nav.value!, {
		duration:1.1, opacity:0, width:"120vw", maxWidth:"120vw",
		onComplete:() => {
			router.push(to);
		}
	});
}

function prev():void {
	slide(-1);
}

function next():void {
	slide(1);
}

function slide(direction:number):void {
	const prevValue = currentIndex.value;

	currentIndex.value += direction;
	if(currentIndex.value < 0) currentIndex.value = boxList.value.length-1;
	if(currentIndex.value > boxList.value.length-1) currentIndex.value = 0;
	direction = prevValue - currentIndex.value;//override direction in case we're looping back

	const boxes = box.value!;
	const vw:number = boxes[0].$el.getBoundingClientRect().width;
	for (let i = 0; i < boxes.length; i++) {
		const b = boxes[i];
		if(currentIndex.value == i) {
			if(direction == 1) b.openFromRight();
			else b.openFromLeft();
		}else{
			if(direction == 1) b.closeToLeft();
			else b.closeToRight();
		}
		gsap.killTweensOf(b.$el);
		gsap.to(b.$el, {duration:1.5, x:-vw * currentIndex.value, ease:"power2.inOut"});

	}
}
</script>

<style scoped lang="less">
.homeview{
	width: 100%;
	display: flex;
	flex-direction: column;
	overflow-x: hidden;

	.boxes {
		display: flex;
		flex-direction: column;
		position: relative;

		.list {
			display: flex;
			flex-direction: row;
			.box {
				width: 100%;
				min-width: 100%;
			}
		}

		.navigation {
			.center();
			top: 45%;
			// overflow: hidden;
			position: absolute;
			pointer-events: none;
			width: calc(100% - 2em);
			max-width: 1200px;
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			z-index: 1;

			// &.noPrev {
				// justify-content: flex-end;
			// }

			button {
				background: none;
				transition: all 1s;
				padding: 2em;
				pointer-events: all;
				img {
					width: 2em;
				}
				&.prev {
					&:hover {
						margin-left: -1em;
						padding-right: 3em;
					}
				}
				&.next {
					&:hover {
						margin-right: -1em;
						padding-left: 3em;
					}
				}
			}
		}
	}

}
@media only screen and (max-width: 480px) {
.homeview{
	.boxes {
		.navigation {
			bottom: -2em;
			position: unset;
			transform: unset;
			top: unset;
			width: 70%;
			margin: auto;

			&.noPrev {
				justify-content: space-between;
			}
			
			button {
				padding: .5em 2em;
				img {
					width: 1.5em;
				}

				&.prev:hover,
				&.next:hover {
					margin-right: unset;
					margin-left: unset;
				}
			}
		}
	}
}
}
</style>